import { Fragment, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import classNames from "classnames/bind";
import styles from "./CenteredModal.module.scss";

const cx = classNames.bind(styles);

let openModalCount = 0;

function CenteredModal({
    show,
    modalType = "",
    title,
    children,
    onClose,
    onConfirm,
    confirmText = "Save Changes",
    closeText = "Cancel",
    submittingText = "Processing...",
    formId,
    disabledConfirmBtn = false,
    isViewMode = false,
    modalHeight,
    isSubmitting = false,
    modalContentClass = "",
    isDrawer = false,
    drawerWidth = "560px",
    confirmBtnClass = "btn btn-bt-primary",
    cancelBtnClass = "btn btn-bt-secondary",
    zIndex = 1060,
    blurred = false
}) {
    const isConfirmModal = Boolean(title && /confirm/i.test(title));
    const isFormModal = Boolean(!isConfirmModal && formId);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (show) {
            openModalCount++;
            document.body.style.overflow = "hidden";

            return () => {
                openModalCount--;
                if (openModalCount === 0) {
                    setTimeout(() => {
                        if (openModalCount === 0) {
                            document.body.style.overflow = "";
                        }
                    }, 250); // wait for exit animation (0.2s) to finish
                }
            };
        }
    }, [show]);

    // ── DRAWER MODE ──────────────────────────────────────────────────────────
    if (isDrawer) {
        const drawerContent = (
            <AnimatePresence>
                {show && (
                    <Fragment>
                        <motion.div
                            className={cx("modalBackdrop", "modal-backdrop fade show")}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.4 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            style={{ zIndex: 1040 }}
                            onClick={onClose}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                            style={{
                                position: "fixed",
                                top: 0,
                                right: 0,
                                height: "100vh",
                                width: drawerWidth,
                                maxWidth: "100vw",
                                zIndex: 1050,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "#ffffff",
                                borderLeft: "1px solid #d5dbdb",
                                boxShadow: "-4px 0 24px rgba(0,28,36,0.15)",
                            }}
                        >
                            {/* Drawer Header */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px 24px",
                                borderBottom: "1px solid #eaeded",
                                flexShrink: 0,
                                backgroundColor: "#ffffff",
                            }}>
                                <div>
                                    <h5 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#16191f" }}>{title}</h5>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close shadow-none"
                                    onClick={onClose}
                                    style={{ opacity: 0.5 }}
                                />
                            </div>

                            {/* Drawer Body — scrollable */}
                            <div style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "24px",
                                backgroundColor: "#ffffff",
                            }}>
                                {children}
                            </div>

                            {/* Drawer Footer — sticky */}
                            {closeText && (
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: "12px",
                                    padding: "14px 24px",
                                    borderTop: "1px solid #eaeded",
                                    backgroundColor: "#f2f3f3",
                                    flexShrink: 0,
                                }}>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        style={{
                                            minWidth: "80px",
                                            padding: "6px 20px",
                                            borderRadius: "4px",
                                            border: "1px solid #aab7b8",
                                            backgroundColor: "#ffffff",
                                            color: "#16191f",
                                            fontSize: "0.875rem",
                                            fontWeight: 700,
                                            cursor: "pointer",
                                        }}
                                    >
                                        {closeText}
                                    </button>
                                    {!isViewMode && (onConfirm || formId) && (
                                        <button
                                            disabled={disabledConfirmBtn || isSubmitting}
                                            type={formId ? "submit" : "button"}
                                            {...(formId && { form: formId })}
                                            {...(!formId && { onClick: onConfirm })}
                                            style={{
                                                minWidth: "110px",
                                                padding: "6px 20px",
                                                borderRadius: "4px",
                                                border: "none",
                                                backgroundColor: "#0972d3",
                                                color: "#ffffff",
                                                fontSize: "0.875rem",
                                                fontWeight: 700,
                                                cursor: disabledConfirmBtn || isSubmitting ? "not-allowed" : "pointer",
                                                opacity: disabledConfirmBtn || isSubmitting ? 0.7 : 1,
                                            }}
                                        >
                                            {isSubmitting && (
                                                <span role="status" aria-hidden="true" className="spinner-border spinner-border-sm me-2" />
                                            )}
                                            {isSubmitting ? submittingText : confirmText}
                                        </button>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </Fragment>
                )}
            </AnimatePresence>
        );
        return typeof document !== "undefined" ? createPortal(drawerContent, document.body) : null;
    }
    // ── END DRAWER MODE ──────────────────────────────────────────────────────

    const modalContent = (
        <AnimatePresence>
            {show && (
                <Fragment>
                    <motion.div
                        className={cx("modalBackdrop", "modal-backdrop fade show")}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.56 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ zIndex: zIndex - 5 }}
                    ></motion.div>

                    <div className={cx("modalRoot", "modal fade show")} style={{ display: "block", zIndex: zIndex, filter: blurred ? "blur(3px) brightness(0.85)" : "none", transition: "filter 0.3s ease", pointerEvents: blurred ? "none" : "auto" }} tabIndex="-1">
                        <div className={cx("modalDialog", `modal-dialog modal-dialog-centered ${modalType}`)}>
                            <motion.div
                                className={cx("modalContent", { confirmModal: isConfirmModal, formModal: isFormModal }, `modal-content shadow-lg ${modalContentClass}`)}
                                style={modalHeight ? { height: modalHeight, overflowY: 'auto' } : {}}
                                initial={{ opacity: 0, scale: 0.95, y: -20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                                {title && (
                                    <div className={cx("modalHeader", { confirmHeader: isConfirmModal, formHeader: isFormModal }, "modal-header")}>
                                        <h5 className={cx("modalTitle", "modal-title fw-bold")}>{title}</h5>
                                        <button type="button" className={cx("closeBtn", "btn-close shadow-none")} onClick={onClose}></button>
                                    </div>
                                )}
                                <div className={cx("modalBody", { confirmBody: isConfirmModal, formBody: isFormModal }, "modal-body")}>
                                    {children}
                                </div>
                                {closeText && (
                                    <div className={cx("modalFooter", { confirmFooter: isConfirmModal, formFooter: isFormModal }, "modal-footer")}>
                                        {!isViewMode && (onConfirm || formId) && (
                                            <button
                                                disabled={disabledConfirmBtn || isSubmitting}
                                                type={formId ? "submit" : "button"}
                                                {...(formId && { form: formId })}
                                                className={cx("confirmBtn", confirmBtnClass)}
                                                {...(!formId && { onClick: onConfirm })}
                                            >
                                                {isSubmitting && (
                                                    <span role="status" aria-hidden="true" className="spinner-border spinner-border-sm me-2"></span>
                                                )}
                                                {isSubmitting ? submittingText : confirmText}
                                            </button>
                                        )}
                                        <button type="button" className={cx("cancelBtn", cancelBtnClass)} onClick={onClose}>
                                            {closeText}
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </Fragment>
            )}
        </AnimatePresence>
    );

    // Render modal at the document body level using a portal
    return typeof document !== "undefined" ? createPortal(modalContent, document.body) : null;
}

export default CenteredModal;
